import React from 'react';
import { RadioSetting } from '../../components/SettingCards/RadioSetting/RadioSetting';
import { KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS } from '../../config/SettingIds';
import { getSettingConfig, RadioSettingConfig } from '../../config/SettingsDefinitions';
import { SettingCard } from '../../components/SettingCards/SettingCard/SettingCard';
import { ToggleSettingGroup } from '../../components/SettingCards/ToggleSetting/ToggleSettingGroup';
import './KeyboardUnicodeModeSetting.css';
import { SettingCardContent } from '../../components/SettingCards/SettingCard/SettingCardContent/SettingCardContent';

interface SettingState {
	value: string | boolean;
	disabled: boolean;
}

export const KeyboardUnicodeModeSetting: React.FC<{
	settingsState: Record<string, SettingState>;
	handleChange: (settingId: string, value: string | boolean) => Promise<void>;
}> = ({ settingsState, handleChange }) => {
	const ctrlCharacterState = settingsState[
		KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.CTRL_CHARACTER
	] || {
		value: 'control',
		disabled: false,
	};
	const altCharacterState = settingsState[
		KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.ALT_CHARACTER
	] || {
		value: 'command',
		disabled: false,
	};
	const windowsLogoKeyState = settingsState[
		KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.WINDOWS_LOGO_KEY
	] || {
		value: false,
		disabled: false,
	};
	const fkeysWithOptionState = settingsState[
		KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.FKEYS_WITH_OPTION
	] || {
		value: false,
		disabled: false,
	};

	const settingConfig = getSettingConfig(
		KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.UNICODE_MODE
	);

	const ctrlCharacterConfig = settingConfig.subSettings[0] as RadioSettingConfig;
	const altCharacterConfig = settingConfig.subSettings[1] as RadioSettingConfig;

	return (
		<>
			<SettingCard
				title={settingConfig.title}
				settingId={KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.UNICODE_MODE}
				showContentSeparator={settingConfig.showContentSeparator ?? false}
				content={
					<>
						<SettingCardContent className="keyboard-unicode-mode-content">
							{ctrlCharacterConfig && (
								<div className="keyboard-unicode-mode-item">
									<RadioSetting
										card={false}
										title={ctrlCharacterConfig.title}
										description={ctrlCharacterConfig.description}
										options={ctrlCharacterConfig.options}
										selectedValue={ctrlCharacterState.value as string}
										onChange={(value) =>
											handleChange(
												KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.CTRL_CHARACTER,
												value
											)
										}
										disabled={ctrlCharacterState.disabled}
										settingId={KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.CTRL_CHARACTER}
									/>
								</div>
							)}
							{altCharacterConfig && (
								<div className="keyboard-unicode-mode-item">
									<RadioSetting
										card={false}
										title={altCharacterConfig.title}
										description={altCharacterConfig.description}
										options={altCharacterConfig.options}
										selectedValue={altCharacterState.value as string}
										onChange={(value) =>
											handleChange(
												KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.ALT_CHARACTER,
												value
											)
										}
										disabled={altCharacterState.disabled}
										settingId={KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.ALT_CHARACTER}
									/>
								</div>
							)}
							<div className="keyboard-unicode-mode-item">
								<ToggleSettingGroup
									settings={[
										{
											settingId: KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.WINDOWS_LOGO_KEY,
											title: 'Send Windows logo key using ⌘ Command (right)',
											checked: windowsLogoKeyState.value as boolean,
											onChange: (value) =>
												handleChange(
													KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.WINDOWS_LOGO_KEY,
													value
												),
											disabled: windowsLogoKeyState.disabled,
										},
										{
											settingId:
												KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.FKEYS_WITH_OPTION,
											title:
												'Send F1-F12 keys with Option (left) and the corresponding keypad keys',
											description:
												'For example, press Option(left)+1(on the numeric keypad) to send the F1',
											checked: fkeysWithOptionState.value as boolean,
											onChange: (value) =>
												handleChange(
													KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.FKEYS_WITH_OPTION,
													value
												),

											disabled: fkeysWithOptionState.disabled,
										},
									]}
								/>
							</div>
						</SettingCardContent>
					</>
				}
			/>
		</>
	);
};
